import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  TenantService,
  TenantInfo,
  TenantBankAccount,
} from '../../shared/services/tenant.service';
import { ToastService } from '@black/ui';
import { environment } from '../../../environments/environment';
import { DAISY_PREBUILT_THEMES, DaisyThemeOption } from '@black/shared';

@Component({
  selector: 'black-academy-settings',
  templateUrl: './academy-settings.component.html',
  standalone: false,
})
export class AcademySettingsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private router = inject(Router);
  private tenantService = inject(TenantService);
  private toastService = inject(ToastService);

  settingsForm!: FormGroup;
  loading = false;
  saving = false;
  currentTenant: TenantInfo | null = null;
  activeTab:
    | 'branding'
    | 'policies'
    | 'legal'
    | 'payments'
    | 'landingPage' = 'branding';

  themeOptions: DaisyThemeOption[] = DAISY_PREBUILT_THEMES;
  themeMode: 'prebuilt' | 'custom' = 'prebuilt';

  uploadingLogo = false;
  uploadingFavicon = false;

  // Visibility toggles for payment gateway credentials
  showPayhereSecret = false;
  showStripeSecretKey = false;
  showStripeWebhookSecret = false;

  ngOnInit(): void {
    this.initForm();
    this.loadTenantData();
  }

  private initForm(): void {
    this.settingsForm = this.fb.group({
      // Branding & General
      name: ['', [Validators.required, Validators.minLength(3)]],
      subdomain: [{ value: '', disabled: true }],
      autoPublishLiveClasses: [true],
      appMode: [false],
      theme: ['light', [Validators.required]],
      primaryColor: ['#3B82F6'],
      primaryContentColor: ['#FFFFFF'],
      secondaryColor: ['#10B981'],
      secondaryContentColor: ['#FFFFFF'],
      accentColor: ['#F59E0B'],
      accentContentColor: ['#000000'],
      neutralColor: ['#1F2937'],
      neutralContentColor: ['#FFFFFF'],
      baseColor: ['#FFFFFF'],
      baseContentColor: ['#1F2937'],
      logo: [''],
      favicon: [''],

      // Legal & Invoicing Settings
      legalIsSystemManaged: [true],
      legalCompanyName: [''],
      legalAddress: [''],
      legalCity: [''],
      legalCountry: [''],
      legalTaxId: [''],
      legalFooterNote: [''],
      legalContactEmail: ['', [Validators.email]],
      legalContactPhone: [''],

      // Payments & Direct Bank Transfer Settings
      paymentsIsSystemManaged: [true],
      acceptBankTransfer: [true],
      acceptPayHere: [false],
      acceptStripe: [false],
      bankName: [''],
      accountName: [''],
      accountNumber: [''],
      branchName: [''],
      swiftCode: [''],
      payhereMerchantId: [''],
      payhereMerchantSecret: [''],
      payhereSandbox: [true],
      stripePublishableKey: [''],
      stripeSecretKey: [''],
      stripeWebhookSecret: [''],

      // Policies (Terms & Conditions, Privacy Policy, Refund Policy)
      termsMode: ['default'],
      termsUrl: [''],
      termsContent: [''],
      privacyMode: ['default'],
      privacyUrl: [''],
      privacyContent: [''],
      refundMode: ['default'],
      refundUrl: [''],
      refundContent: [''],
    });
  }

  private loadTenantData(): void {
    this.loading = true;
    this.http
      .get<{
        success: boolean;
        data: TenantInfo;
      }>(`${environment.host}/api/v1/tenants/my-tenant`)
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res.success && res.data) {
            this.populateTenantData(res.data);
          } else {
            this.fallbackToCurrentTenant();
          }
        },
        error: () => {
          this.fallbackToCurrentTenant();
        },
      });
  }

  private fallbackToCurrentTenant(): void {
    this.tenantService.currentTenant$.subscribe((tenant) => {
      if (tenant) {
        this.populateTenantData(tenant);
      }
      this.loading = false;
    });
  }

  private populateTenantData(tenant: TenantInfo): void {
    this.currentTenant = tenant;
    const b = tenant.settings?.branding || {};
    const features = tenant.settings?.features || {};
    const savedTheme = b.theme || 'light';
    this.themeMode = savedTheme === 'custom' ? 'custom' : 'prebuilt';

    const legal = tenant.settings?.legal || {};
    const payments = tenant.settings?.payments || {};
    const bankAccounts = (payments.bankAccounts || []) as TenantBankAccount[];
    const bankAccount = bankAccounts[0] || ({} as Partial<TenantBankAccount>);
    const payhere = payments.payhere || {};
    const stripe = payments.stripe || {};
    const acceptedMethods: string[] = payments.acceptedMethods || [
      'BANK_TRANSFER',
    ];

    this.settingsForm.patchValue({
      name: tenant.name || '',
      subdomain: tenant.subdomain || '',
      autoPublishLiveClasses: features.autoPublishLiveClasses ?? true,
      theme: savedTheme,
      primaryColor: b.primaryColor || '#3B82F6',
      primaryContentColor: b.primaryContentColor || '#FFFFFF',
      secondaryColor: b.secondaryColor || '#10B981',
      secondaryContentColor: b.secondaryContentColor || '#FFFFFF',
      accentColor: b.accentColor || '#F59E0B',
      accentContentColor: b.accentContentColor || '#000000',
      neutralColor: b.neutralColor || '#1F2937',
      neutralContentColor: b.neutralContentColor || '#FFFFFF',
      baseColor: b.baseColor || '#FFFFFF',
      baseContentColor: b.baseContentColor || '#1F2937',
      logo: b.logo || '',
      favicon: b.favicon || '',

      // Legal
      legalIsSystemManaged: legal.isSystemManaged !== false,
      legalCompanyName: legal.companyName || '',
      legalAddress: legal.address || '',
      legalCity: legal.city || '',
      legalCountry: legal.country || '',
      legalTaxId: legal.taxId || '',
      legalFooterNote: legal.footerNote || '',
      legalContactEmail: legal.contactEmail || '',
      legalContactPhone: legal.contactPhone || '',

      // Payments & Direct Bank Transfer
      paymentsIsSystemManaged: payments.isSystemManaged !== false,
      acceptBankTransfer: acceptedMethods.includes('BANK_TRANSFER'),
      acceptPayHere: acceptedMethods.includes('PAYHERE'),
      acceptStripe: acceptedMethods.includes('STRIPE'),
      bankName: bankAccount.bankName || '',
      accountName: bankAccount.accountName || '',
      accountNumber: bankAccount.accountNumber || '',
      branchName: bankAccount.branchName || '',
      swiftCode: bankAccount.swiftCode || '',
      payhereMerchantId: payhere.merchantId || '',
      payhereMerchantSecret: payhere.merchantSecret || '',
      payhereSandbox: payhere.sandbox !== false,
      stripePublishableKey: stripe.publishableKey || '',
      stripeSecretKey: stripe.secretKey || '',
      stripeWebhookSecret: stripe.webhookSecret || '',

      // Policies
      termsMode: tenant.settings?.policies?.termsMode || 'default',
      termsUrl: tenant.settings?.policies?.termsUrl || '',
      termsContent: tenant.settings?.policies?.termsContent || '',
      privacyMode: tenant.settings?.policies?.privacyMode || 'default',
      privacyUrl: tenant.settings?.policies?.privacyUrl || '',
      privacyContent: tenant.settings?.policies?.privacyContent || '',
      refundMode: tenant.settings?.policies?.refundMode || 'default',
      refundUrl: tenant.settings?.policies?.refundUrl || '',
      refundContent: tenant.settings?.policies?.refundContent || '',
      appMode:
        tenant.appMode !== undefined
          ? tenant.appMode
          : tenant.slug !== 'default',
    });

    this.applyCurrentBranding();
  }

  setActiveTab(
    tab:
      | 'branding'
      | 'policies'
      | 'legal'
      | 'payments'
      | 'landingPage',
  ): void {
    this.activeTab = tab;
  }

  openSiteStudio(): void {
    const opened = window.open('/site-editor', '_blank');
    if (!opened) {
      this.router.navigate(['/site-editor']);
    }
  }

  loadTermsTemplate(): void {
    const current = this.settingsForm.get('termsContent')?.value;
    if (current && current.trim().length > 0) {
      const confirmed = window.confirm(
        'Loading the boilerplate template will replace your current Terms & Conditions text. Do you want to proceed?',
      );
      if (!confirmed) return;
    }

    const academyName = this.currentTenant?.name || 'Our Academy';
    const contactEmail =
      this.currentTenant?.settings?.legal?.contactEmail ||
      `support@${this.currentTenant?.subdomain || 'learnbird'}.lk`;

    this.settingsForm.patchValue({
      termsContent: this.getDefaultTermsTemplate(academyName, contactEmail),
    });
    this.toastService.showToast(
      'Terms and Conditions template loaded.',
      'success',
    );
  }

  loadPrivacyTemplate(): void {
    const current = this.settingsForm.get('privacyContent')?.value;
    if (current && current.trim().length > 0) {
      const confirmed = window.confirm(
        'Loading the boilerplate template will replace your current Privacy Policy text. Do you want to proceed?',
      );
      if (!confirmed) return;
    }

    const academyName = this.currentTenant?.name || 'Our Academy';
    const contactEmail =
      this.currentTenant?.settings?.legal?.contactEmail ||
      `privacy@${this.currentTenant?.subdomain || 'learnbird'}.lk`;

    this.settingsForm.patchValue({
      privacyContent: this.getDefaultPrivacyTemplate(academyName, contactEmail),
    });
    this.toastService.showToast('Privacy Policy template loaded.', 'success');
  }

  getDefaultTermsTemplate(academyName: string, contactEmail: string): string {
    return `<h2>Terms and Conditions</h2>
<p>Welcome to <strong>${academyName}</strong>. By accessing or using our platform, courses, and educational services, you agree to be bound by these Terms and Conditions.</p>

<h3>1. Accounts and Registration</h3>
<p>To access certain courses and features, you may need to register an account. You must provide accurate and complete information and keep your account credentials secure. You are responsible for all activities occurring under your account.</p>

<h3>2. Course Enrollment and Access</h3>
<p>When you enroll in a course or live class on ${academyName}, you are granted a non-exclusive, non-transferable license to access and view the course materials for personal, educational purposes. You may not distribute, reproduce, or resell any course recordings or materials.</p>

<h3>3. Payments and Subscriptions</h3>
<p>Course fees, class enrollment fees, and any subscription charges are clearly stated before purchase. All payments must be made using authorized payment methods. Payments are non-refundable unless explicitly stated under our Refund Policy.</p>

<h3>4. Code of Conduct</h3>
<p>Students and participants must maintain respectful and professional behavior during all live classes and community discussions. Harassment, disruptive behavior, or violations of academic integrity may result in immediate suspension without refund.</p>

<h3>5. Intellectual Property</h3>
<p>All content, trademarks, course curricula, video streams, and presentation slides provided on ${academyName} remain the exclusive property of ${academyName} and its instructors.</p>

<h3>6. Limitation of Liability</h3>
<p>${academyName} strives to maintain uninterrupted, high-quality services, but does not guarantee continuous availability. We shall not be liable for any indirect, incidental, or consequential damages resulting from platform use.</p>

<h3>7. Contact Us</h3>
<p>If you have any questions regarding these Terms and Conditions, please contact us at <a href="mailto:${contactEmail}">${contactEmail}</a>.</p>`;
  }

  getDefaultPrivacyTemplate(academyName: string, contactEmail: string): string {
    return `<h2>Privacy Policy</h2>
<p>At <strong>${academyName}</strong>, we are committed to safeguarding the privacy and personal data of our students, instructors, and visitors. This Privacy Policy explains how we collect, use, and protect your information.</p>

<h3>1. Information We Collect</h3>
<ul>
  <li><strong>Account Information:</strong> Name, email address, contact phone number, and profile details provided during registration.</li>
  <li><strong>Educational Records:</strong> Course enrollments, attendance in live sessions, assignment submissions, and progress tracking.</li>
  <li><strong>Payment Information:</strong> Transaction identifiers and payment confirmation (payment card and sensitive banking details are processed directly by our secure payment gateways).</li>
  <li><strong>Technical Information:</strong> Device type, browser information, IP address, and platform usage metrics.</li>
</ul>

<h3>2. How We Use Your Information</h3>
<p>We use your personal data to:</p>
<ul>
  <li>Provide and manage your access to courses, live classes, and educational materials.</li>
  <li>Send essential notifications regarding class schedules, course updates, and account security.</li>
  <li>Process transactions and issue receipts or invoices.</li>
  <li>Enhance platform performance, user experience, and course quality.</li>
</ul>

<h3>3. Data Protection and Security</h3>
<p>We implement robust technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Sensitive communications are encrypted in transit.</p>

<h3>4. Sharing of Information</h3>
<p>We do not sell or rent your personal information to third parties. We share data only with verified service providers strictly necessary to deliver our services.</p>

<h3>5. Your Rights and Choices</h3>
<p>You have the right to access, review, update, or request the deletion of your personal data. You may update your profile directly through your account dashboard or by contacting our team.</p>

<h3>6. Contact Us</h3>
<p>For any inquiries or requests regarding this Privacy Policy and your personal data, please contact our privacy representative at <a href="mailto:${contactEmail}">${contactEmail}</a>.</p>`;
  }

  loadRefundTemplate(): void {
    const current = this.settingsForm.get('refundContent')?.value;
    if (current && current.trim().length > 0) {
      const confirmed = window.confirm(
        'Loading the boilerplate template will replace your current Refund Policy text. Do you want to proceed?',
      );
      if (!confirmed) return;
    }

    const academyName = this.currentTenant?.name || 'Our Academy';
    const contactEmail =
      this.currentTenant?.settings?.legal?.contactEmail ||
      `billing@${this.currentTenant?.subdomain || 'learnbird'}.lk`;

    this.settingsForm.patchValue({
      refundContent: this.getDefaultRefundTemplate(academyName, contactEmail),
    });
    this.toastService.showToast('Refund Policy template loaded.', 'success');
  }

  getDefaultRefundTemplate(academyName: string, contactEmail: string): string {
    return `<h2>Refund Policy</h2>
<p>At <strong>${academyName}</strong>, we strive to provide high-quality courses and learning experiences. This Refund Policy outlines the terms and conditions under which refunds are evaluated and issued.</p>

<h3>1. Eligibility for Refunds</h3>
<p>Refund requests are considered under the following conditions:</p>
<ul>
  <li><strong>Live Courses & Subscriptions:</strong> Requests submitted within 7 calendar days from the initial enrollment or billing date, provided that the student has attended no more than one live class session and accessed less than 20% of the course materials.</li>
  <li><strong>Technical Issues:</strong> If you experience verifiable technical issues preventing access to the course content or live classes that our technical support team is unable to resolve within 72 hours.</li>
  <li><strong>Class Cancellation:</strong> If a scheduled course or class series is cancelled by ${academyName}, students will receive a full 100% refund or pro-rated credit towards another course.</li>
</ul>

<h3>2. Non-Refundable Circumstances</h3>
<p>Refunds will not be granted under the following circumstances:</p>
<ul>
  <li>Requests made after the 7-day refund request window has expired.</li>
  <li>Courses where certificates of completion have already been awarded or substantial course materials have been downloaded.</li>
  <li>Suspension or termination of an account resulting from a violation of our Code of Conduct or Terms and Conditions.</li>
</ul>

<h3>3. How to Request a Refund</h3>
<p>To initiate a refund request, please email our support team at <a href="mailto:${contactEmail}">${contactEmail}</a> with:</p>
<ul>
  <li>Your full name and registered account email address.</li>
  <li>Course title and transaction or receipt number.</li>
  <li>A detailed reason for the refund request.</li>
</ul>

<h3>4. Processing Times</h3>
<p>Once approved, refunds are processed within 7 to 10 business days. Refunds will be issued using the original payment method used during enrollment (or via direct bank transfer where applicable).</p>

<h3>5. Contact Us</h3>
<p>If you have any questions or require assistance regarding refunds, please contact us at <a href="mailto:${contactEmail}">${contactEmail}</a>.</p>`;
  }

  get isLegalCustomEnabled(): boolean {
    return this.currentTenant?.settings?.legal?.isSystemManaged === false;
  }

  get isPaymentsCustomEnabled(): boolean {
    return this.currentTenant?.settings?.payments?.isSystemManaged === false;
  }

  setThemeMode(mode: 'prebuilt' | 'custom'): void {
    this.themeMode = mode;
    if (mode === 'custom') {
      this.settingsForm.patchValue({ theme: 'custom' });
    } else {
      if (this.settingsForm.value.theme === 'custom') {
        this.settingsForm.patchValue({ theme: 'light' });
      }
    }
    this.applyCurrentBranding();
  }

  selectPrebuiltTheme(themeId: string): void {
    this.themeMode = 'prebuilt';
    this.settingsForm.patchValue({ theme: themeId });

    const prebuilt = this.themeOptions.find((t) => t.id === themeId);
    if (prebuilt) {
      this.settingsForm.patchValue({
        primaryColor: prebuilt.primary,
        secondaryColor: prebuilt.secondary,
        accentColor: prebuilt.accent,
        baseColor: prebuilt.base100,
      });
    }

    this.applyCurrentBranding();
  }

  onBrandingChange(): void {
    this.applyCurrentBranding();
  }

  onAssetUpload(event: Event, targetField: 'logo' | 'favicon'): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const formData = new FormData();
    formData.append('file', file);

    if (targetField === 'logo') this.uploadingLogo = true;
    if (targetField === 'favicon') this.uploadingFavicon = true;

    this.http
      .post<{
        url?: string;
        publicUrl?: string;
      }>(`${environment.host}/api/v1/upload/asset`, formData)
      .subscribe({
        next: (res) => {
          const uploadedUrl = res.url || res.publicUrl || '';
          this.settingsForm.patchValue({ [targetField]: uploadedUrl });
          this.applyCurrentBranding();
          this.toastService.showToast(
            `${targetField === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully.`,
            'success',
          );
          if (targetField === 'logo') this.uploadingLogo = false;
          if (targetField === 'favicon') this.uploadingFavicon = false;
        },
        error: (err) => {
          if (targetField === 'logo') this.uploadingLogo = false;
          if (targetField === 'favicon') this.uploadingFavicon = false;
          this.toastService.showToast(
            `Failed to upload ${targetField}: ${err?.error?.message || err?.message || 'Upload failed'}`,
            'error',
          );
        },
      });
  }

  clearAsset(targetField: 'logo' | 'favicon'): void {
    this.settingsForm.patchValue({ [targetField]: '' });
    this.applyCurrentBranding();
  }

  private applyCurrentBranding(): void {
    const val = this.settingsForm.value;
    const isCustom = this.themeMode === 'custom' || val.theme === 'custom';
    this.tenantService.applyTenantBranding({
      theme: isCustom ? 'custom' : val.theme,
      primaryColor: isCustom ? val.primaryColor : undefined,
      primaryContentColor: isCustom ? val.primaryContentColor : undefined,
      secondaryColor: isCustom ? val.secondaryColor : undefined,
      secondaryContentColor: isCustom ? val.secondaryContentColor : undefined,
      accentColor: isCustom ? val.accentColor : undefined,
      accentContentColor: isCustom ? val.accentContentColor : undefined,
      neutralColor: isCustom ? val.neutralColor : undefined,
      neutralContentColor: isCustom ? val.neutralContentColor : undefined,
      baseColor: isCustom ? val.baseColor : undefined,
      baseContentColor: isCustom ? val.baseContentColor : undefined,
      logo: val.logo,
      favicon: val.favicon,
    });
  }

  private cleanEmptyValues(obj: any): any {
    if (obj === null || obj === undefined || obj === '') return undefined;
    if (typeof obj !== 'object' || obj instanceof Date) return obj;
    if (Array.isArray(obj)) {
      const cleanedArr = obj
        .map((item) => this.cleanEmptyValues(item))
        .filter((item) => item !== undefined);
      return cleanedArr.length > 0 ? cleanedArr : undefined;
    }

    const cleanedObj: Record<string, any> = {};
    for (const [key, val] of Object.entries(obj)) {
      const cleanedVal = this.cleanEmptyValues(val);
      if (cleanedVal !== undefined) {
        cleanedObj[key] = cleanedVal;
      }
    }

    return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined;
  }

  onSubmit(): void {
    if (this.settingsForm.invalid) {
      this.toastService.showToast('Please check all required fields.', 'error');
      return;
    }

    this.saving = true;
    const formVal = this.settingsForm.getRawValue();
    const finalTheme = this.themeMode === 'custom' ? 'custom' : formVal.theme;
    const isCustom = finalTheme === 'custom';

    const acceptedMethods: string[] = [];
    if (formVal.acceptBankTransfer) acceptedMethods.push('BANK_TRANSFER');
    if (formVal.acceptPayHere) acceptedMethods.push('PAYHERE');
    if (formVal.acceptStripe) acceptedMethods.push('STRIPE');

    let paymentsPayload: any = undefined;
    if (this.isPaymentsCustomEnabled) {
      paymentsPayload = {
        ...(this.currentTenant?.settings?.payments || {}),
        isSystemManaged: false,
      };

      if (acceptedMethods.length > 0) {
        paymentsPayload.acceptedMethods = acceptedMethods;
      }

      if (
        formVal.payhereMerchantId?.trim() ||
        formVal.payhereMerchantSecret?.trim()
      ) {
        paymentsPayload.payhere = {
          merchantId: formVal.payhereMerchantId?.trim() || undefined,
          merchantSecret: formVal.payhereMerchantSecret?.trim() || undefined,
          sandbox: formVal.payhereSandbox,
        };
      }

      const stripeClean = this.cleanEmptyValues({
        publishableKey: formVal.stripePublishableKey?.trim(),
        secretKey: formVal.stripeSecretKey?.trim(),
        webhookSecret: formVal.stripeWebhookSecret?.trim(),
      });
      if (stripeClean) {
        paymentsPayload.stripe = stripeClean;
      }

      if (formVal.bankName?.trim() || formVal.accountNumber?.trim()) {
        paymentsPayload.bankAccounts = [
          {
            bankName: formVal.bankName?.trim() || '',
            accountName: formVal.accountName?.trim() || '',
            accountNumber: formVal.accountNumber?.trim() || '',
            branchName: formVal.branchName?.trim() || '',
            swiftCode: formVal.swiftCode?.trim() || undefined,
          },
        ];
      }
    }

    let legalPayload: any = undefined;
    if (this.isLegalCustomEnabled) {
      const legalClean = this.cleanEmptyValues({
        companyName: formVal.legalCompanyName?.trim(),
        address: formVal.legalAddress?.trim(),
        city: formVal.legalCity?.trim(),
        country: formVal.legalCountry?.trim(),
        taxId: formVal.legalTaxId?.trim(),
        footerNote: formVal.legalFooterNote?.trim(),
        contactEmail: formVal.legalContactEmail?.trim(),
        contactPhone: formVal.legalContactPhone?.trim(),
      });
      legalPayload = {
        ...(this.currentTenant?.settings?.legal || {}),
        isSystemManaged: false,
        ...(legalClean || {}),
      };
    }

    const updatePayload: any = {
      name: formVal.name?.trim(),
      appMode: Boolean(formVal.appMode),
      settings: {
        ...(this.currentTenant?.settings || {}),
        branding: {
          ...(this.currentTenant?.settings?.branding || {}),
          theme: finalTheme,
          logo: formVal.logo?.trim() || undefined,
          favicon: formVal.favicon?.trim() || undefined,
          primaryColor: isCustom ? formVal.primaryColor : undefined,
          primaryContentColor: isCustom
            ? formVal.primaryContentColor
            : undefined,
          secondaryColor: isCustom ? formVal.secondaryColor : undefined,
          secondaryContentColor: isCustom
            ? formVal.secondaryContentColor
            : undefined,
          accentColor: isCustom ? formVal.accentColor : undefined,
          accentContentColor: isCustom ? formVal.accentContentColor : undefined,
          neutralColor: isCustom ? formVal.neutralColor : undefined,
          neutralContentColor: isCustom
            ? formVal.neutralContentColor
            : undefined,
          baseColor: isCustom ? formVal.baseColor : undefined,
          baseContentColor: isCustom ? formVal.baseContentColor : undefined,
        },
        features: {
          ...(this.currentTenant?.settings?.features || {}),
          autoPublishLiveClasses: Boolean(formVal.autoPublishLiveClasses),
        },
        ...(legalPayload !== undefined && { legal: legalPayload }),
        ...(paymentsPayload !== undefined && { payments: paymentsPayload }),
        policies: {
          termsMode: formVal.termsMode || 'default',
          termsUrl: formVal.termsUrl?.trim() || undefined,
          termsContent: formVal.termsContent || undefined,
          privacyMode: formVal.privacyMode || 'default',
          privacyUrl: formVal.privacyUrl?.trim() || undefined,
          privacyContent: formVal.privacyContent || undefined,
          refundMode: formVal.refundMode || 'default',
          refundUrl: formVal.refundUrl?.trim() || undefined,
          refundContent: formVal.refundContent || undefined,
        },
        notifications: this.currentTenant?.settings?.notifications,
      },
    };

    this.http
      .put<{
        success: boolean;
        data: TenantInfo;
        message: string;
      }>(`${environment.host}/api/v1/tenants/my-tenant`, updatePayload)
      .subscribe({
        next: (res) => {
          this.saving = false;
          if (res.success && res.data) {
            this.currentTenant = res.data;
            this.toastService.showToast(
              res.message || 'Academy settings updated successfully!',
              'success',
            );
            this.tenantService.applyTenantBranding(res.data.settings?.branding);
          }
        },
        error: (err) => {
          this.saving = false;
          this.toastService.showToast(
            err?.error?.message || 'Failed to update academy settings.',
            'error',
          );
        },
      });
  }
}
